import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Check, RefreshCw, Search, Warehouse, X } from "lucide-react";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import { PRODUCT_FILTER_NONE } from "../../../data/productStore.types";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Button } from "../../../components/Button";
import { Dropdown } from "../../../components/Dropdown";
import { SelectInput } from "../../../components/SelectInput";
import { formatMoneyAmountInputValue } from "../../../utils/parseMoneyAmountInput";
import type { StockLocationId } from "@KassaroAB/shared/types/ids";
import { ColumnVisibilityMenu } from "./ColumnVisibilityMenu";
import { ProductFilterMenu } from "./ProductFilterMenu";
import { useProductsTable } from "./productsTableContext";

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px;
  border-bottom: 1px solid ${colors.border};
`;

const SearchField = styled.div`
  position: relative;
  flex: 1;
  max-width: 360px;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${spacing.x1}px;
  color: ${colors.textMuted};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${spacing.x1}px ${spacing.x1}px ${spacing.x1}px ${spacing.x4}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  font-size: ${fontSizes.regular}px;
  background-color: ${colors.background};
  color: ${colors.text};

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const SaveTargetField = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  white-space: nowrap;
`;

const SaveTargetSelect = styled(SelectInput)`
  width: auto;
`;

const Spacer = styled.div`
  flex: 1;
`;

const ChipsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px;
  border-bottom: 1px solid ${colors.border};
  min-height: 36px;
  box-sizing: border-box;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x05}px;
  padding: ${spacing.x05}px ${spacing.x1}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.small}px;
  background-color: ${colors.background};
  color: ${colors.text};
  font-size: ${fontSizes.small}px;
  cursor: pointer;
`;

const MenuOption = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  width: 100%;
  padding: ${spacing.x1}px;
  border: none;
  border-radius: ${borderRadius.small}px;
  background: transparent;
  color: ${colors.text};
  font-size: ${fontSizes.regular}px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${colors.background};
  }
`;

const MenuCheck = styled(Check)<{ $visible: boolean }>`
  flex-shrink: 0;
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
`;

const Spin = styled(RefreshCw)<{ $spinning?: boolean }>`
  ${({ $spinning }) =>
    $spinning &&
    `
    animation: spin 1s linear infinite;
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}
`;

export const ProductsTableToolbar = () => {
  const { t } = useTranslation();
  const {
    canEdit,
    status,
    resync,
    forceResync,
    toolbarActions,
    columnLabels,
    groups,
    bookkeepingAccounts,
    filters,
    edits,
    stock,
    stockLocations,
    stockLocationId,
    defaultStockLocationId,
    setStockLocationId,
    showStockWarningFilter,
    showChainSaveTarget,
    chainSaveTarget,
    setChainSaveTarget,
    suppliers,
    units,
  } = useProductsTable();

  const {
    search,
    setSearch,
    groupId,
    setGroupId,
    typeFilter,
    setTypeFilter,
    vatFilter,
    setVatFilter,
    stockFilter,
    setStockFilter,
    bookkeepingAccountFilter,
    setBookkeepingAccountFilter,
    variantsFilter,
    setVariantsFilter,
    supplierFilter,
    setSupplierFilter,
    unitFilter,
    setUnitFilter,
    updatedWithinDays,
    setUpdatedWithinDays,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    stockWarningFilter,
    setStockWarningFilter,
    columnVisibility,
    setColumnVisibility,
    sorting,
    setSorting,
  } = filters;

  const optionName = (
    options: { id: string; name: string }[],
    id: string,
    noneLabel = t("productsView.filterNone"),
  ) =>
    id === PRODUCT_FILTER_NONE
      ? noneLabel
      : (options.find((option) => option.id === id)?.name ?? id);
  const presenceLabel = (value: string, hasKey: string, noneKey: string) =>
    t(value === "has" ? hasKey : noneKey);
  const priceLabel = () => {
    const min = formatMoneyAmountInputValue(priceMin);
    const max = formatMoneyAmountInputValue(priceMax);
    if (priceMin !== null && priceMax !== null) {
      return t("productsView.filterChipPrice", { value: `${min}–${max}` });
    }
    return priceMin !== null
      ? t("productsView.filterChipPriceMin", { value: min })
      : t("productsView.filterChipPriceMax", { value: max });
  };

  type FilterChip = { key: string; label: string; clear: () => void };
  const chips = [
    search !== "" && {
      key: "search",
      label: t("productsView.filterChipSearch", { value: search }),
      clear: () => setSearch(""),
    },
    showStockWarningFilter &&
      stockWarningFilter !== null && {
        key: "stockWarning",
        label: t(
          stockWarningFilter === "out"
            ? "productsView.filterStockWarningOut"
            : "productsView.filterStockWarningLow",
        ),
        clear: () => setStockWarningFilter(null),
      },
    variantsFilter !== null && {
      key: "variants",
      label: presenceLabel(
        variantsFilter,
        "productsView.filterVariantsHas",
        "productsView.filterVariantsNone",
      ),
      clear: () => setVariantsFilter(null),
    },
    groupId !== null && {
      key: "group",
      label: t("productsView.filterChipGroup", {
        value: optionName(groups, groupId),
      }),
      clear: () => setGroupId(null),
    },
    supplierFilter !== null && {
      key: "supplier",
      label: t("productsView.filterChipSupplier", {
        value: optionName(suppliers, supplierFilter),
      }),
      clear: () => setSupplierFilter(null),
    },
    vatFilter !== null && {
      key: "vat",
      label: t("productsView.filterChipVat", { value: `${vatFilter}%` }),
      clear: () => setVatFilter(null),
    },
    typeFilter !== null && {
      key: "type",
      label: t("productsView.filterChipType", {
        value: t(`productForm.type${typeFilter as ProductType}`),
      }),
      clear: () => setTypeFilter(null),
    },
    unitFilter !== null && {
      key: "unit",
      label: t("productsView.filterChipUnit", {
        value: optionName(units, unitFilter),
      }),
      clear: () => setUnitFilter(null),
    },
    bookkeepingAccountFilter !== null && {
      key: "bookkeeping",
      label: t("productsView.filterChipBookkeeping", {
        value: optionName(
          bookkeepingAccounts,
          bookkeepingAccountFilter,
          t("productsView.filterBookkeepingDefault"),
        ),
      }),
      clear: () => setBookkeepingAccountFilter(null),
    },
    stockFilter !== null &&
      stockLocationId === null && {
      key: "stock",
      label: presenceLabel(
        stockFilter,
        "productsView.filterChipStockHas",
        "productsView.filterChipStockNone",
      ),
      clear: () => setStockFilter(null),
    },
    updatedWithinDays !== null && {
      key: "updated",
      label: t("productsView.filterChipUpdated", {
        value: t("productsView.filterUpdatedWithin", {
          count: updatedWithinDays,
        }),
      }),
      clear: () => setUpdatedWithinDays(null),
    },
    (priceMin !== null || priceMax !== null) && {
      key: "price",
      label: priceLabel(),
      clear: () => {
        setPriceMin(null);
        setPriceMax(null);
      },
    },
  ].filter((chip): chip is FilterChip => !!chip);
  const isSyncing = status === "syncing";
  const editCount = canEdit ? edits.editCount + stock.editCount : 0;

  const stockLocationOptions: { id: StockLocationId | null; label: string }[] =
    [
      ...(defaultStockLocationId === null
        ? [{ id: null, label: t("productsView.stockAtProduct") }]
        : []),
      ...stockLocations.map((location) => ({
        id: location.id,
        label: location.name,
      })),
    ];
  const stockLocationLabel = stockLocationOptions.find(
    (option) => option.id === stockLocationId,
  )?.label;

  const changeStockLocation = (next: StockLocationId | null) => {
    setStockLocationId(next);
    if (next) {
      setStockFilter(null);
      if (sorting.some((sort) => sort.id === "stockCount")) {
        setSorting([]);
      }
    }
  };

  return (
    <>
      <Header>
        <SearchField>
          <SearchIcon size={16} />
          <SearchInput
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("productsView.searchPlaceholder")}
          />
        </SearchField>

        <Spacer />

        <ProductFilterMenu />

        {stockLocations.length > 0 && (
          <Dropdown
            small
            label={
              <Label title={t("productsView.stockLocation")}>
                <Warehouse size={16} />
                {stockLocationLabel}
              </Label>
            }
          >
            {(close) =>
              stockLocationOptions.map((option) => (
                <MenuOption
                  key={option.id ?? "product"}
                  type="button"
                  onClick={() => {
                    changeStockLocation(option.id);
                    close();
                  }}
                >
                  <MenuCheck
                    size={16}
                    $visible={option.id === stockLocationId}
                  />
                  {option.label}
                </MenuOption>
              ))
            }
          </Dropdown>
        )}

        {showChainSaveTarget && (
          <SaveTargetField
            title={
              edits.editCount > 0
                ? t("productsView.chainSaveTargetLocked")
                : undefined
            }
          >
            {t("productsView.chainSaveTarget")}
            <SaveTargetSelect
              small
              value={chainSaveTarget}
              disabled={edits.editCount > 0}
              onChange={(event) =>
                setChainSaveTarget(event.target.value === "store" ? "store" : "chain")
              }
            >
              <option value="chain">{t("productsView.chainSaveTargetChain")}</option>
              <option value="store">{t("productsView.chainSaveTargetStore")}</option>
            </SaveTargetSelect>
          </SaveTargetField>
        )}

        <ColumnVisibilityMenu
          columnLabels={columnLabels}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
        />

        <Button
          type="button"
          $color="secondary"
          $variant="outlined"
          $small
          title={t("productsView.syncHint")}
          onClick={(event) =>
            event.shiftKey ? void forceResync() : void resync()
          }
          disabled={isSyncing}
        >
          <Label>
            <Spin size={16} $spinning={isSyncing} />
            {t("productsView.sync")}
          </Label>
        </Button>

        {editCount > 0 && (
          <Button
            type="button"
            $small
            onClick={() => void Promise.all([edits.save(), stock.save()])}
            disabled={edits.isSaving || stock.isSaving}
          >
            {t("productsView.saveChanges", { count: editCount })}
          </Button>
        )}

        {toolbarActions}
      </Header>

      {chips.length > 0 && (
        <ChipsRow>
          {chips.map((chip) => (
            <Chip key={chip.key} type="button" onClick={chip.clear}>
              {chip.label}
              <X size={12} />
            </Chip>
          ))}
        </ChipsRow>
      )}
    </>
  );
};
